import React from 'react';
import { useSelector } from 'react-redux';
import { Typography, List, ListItem, ListItemText, ListItemIcon, Link, Box } from '@mui/material';
import { getCurrentMedicalDeviceLabel } from 'data/medicalDevice';
import { getCurrentPdf } from 'data/instructionsForUse';
import medicalDeviceIcon from 'assets/images/medical_device.svg';
import versionIcon from 'assets/images/version.svg';
import manufacturerIcon from 'assets/images/manufacturer.svg';
import releaseIcon from 'assets/images/release.svg';
import eqlIcon from 'assets/images/eql-black.svg';
import cautionIcon from 'assets/images/caution.svg';
import infoBookIcon from 'assets/images/info-book.svg';
import UkCaIcon from 'assets/images/uk-ca.svg';

const AboutThisPortal = () => {
    const customer = useSelector<any, any>((state) => state.AuthReducer.organisation);
    const isAccessPlusAgent = customer?.phio_agent === 'phio_access_plus';
    const isPhioTriageAgent = customer?.phio_agent === 'phio_triage_snowball';

    const [medicalDeviceVersionStr, setMedicalDeviceVersionStr] = React.useState('');
    const [medicalDeviceReleaseDateStr, setMedicalDeviceReleaseDateStr] = React.useState('');
    const [instructionsForUseUrl, setInstructionsForUseUrl] = React.useState('');

    React.useEffect(() => {
        (async () => {
            const result = await getCurrentMedicalDeviceLabel();

            if (result) {
                const { currentVersion, releaseDate } = result;

                if (!currentVersion) {
                    setMedicalDeviceVersionStr('Unknown');
                    return;
                }

                if (!releaseDate) {
                    setMedicalDeviceReleaseDateStr('Unknown');
                    return;
                }

                setMedicalDeviceVersionStr(currentVersion);
                setMedicalDeviceReleaseDateStr(releaseDate);
            }
        })();
    }, []);

    React.useEffect(() => {
        (async () => {
            const result = await getCurrentPdf();

            if (result) {
                const { currentFileUrl } = result;

                if (!currentFileUrl) {
                    return;
                }

                setInstructionsForUseUrl(currentFileUrl);
            }
        })();
    }, []);

    if (isAccessPlusAgent) {
        return (
            <>
                <Typography variant="h5" component="h1" gutterBottom fontWeight="bold">
                    About Phio Access Portal
                </Typography>
                <Typography variant="body1" sx={{ marginTop: '16px' }} mb={2}>
                    The primary purpose of <b>Phio Access Plus Admin Portal</b> is intended to
                    provide Secondary Users access to the information gathered from the Primary user
                    about their MSK pain, injury or dysfunction and the referral care pathway
                    outcome.
                </Typography>
                <Typography variant="h6" component="h2" gutterBottom fontWeight="bold">
                    How to use the Portal
                </Typography>
                <Typography variant="body1" mb={3}>
                    Use the interface provided to render PDF reports for any patients that have
                    interacted with Phio Access Plus.{' '}
                </Typography>
                <Typography variant="h6" component="h2" gutterBottom fontWeight="bold">
                    When to use the Portal
                </Typography>
                <Typography variant="body1" mb={3}>
                    Use this portal when a patient has interacted with Phio Access Plus so that you
                    can access the conversation transcript and the referral care pathway outcome.
                </Typography>
                <Typography variant="h6" component="h2" gutterBottom fontWeight="bold">
                    How the Portal works
                </Typography>
                <Typography variant="body1" mb={3}>
                    The portal will display all data gathered from the primary user by Phio Access
                    Plus and on request render this information into the PDF template to be
                    downloaded.
                </Typography>
                <Typography variant="h6" component="h2" gutterBottom fontWeight="bold">
                    Our Policies
                </Typography>
                <Typography variant="body1" mb={3}>
                    Our policies tell you important information about using the portal.
                </Typography>
                <Box mb={3}>
                    <ul>
                        <li>
                            <Link
                                href="https://phio.eql.ai/privacy"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Privacy policy
                            </Link>
                        </li>
                    </ul>
                </Box>
                <Typography variant="h6" component="h2" fontWeight="bold">
                    Software Information
                </Typography>
                <List>
                    <ListItem>
                        <ListItemIcon>
                            <img src={medicalDeviceIcon} alt="medical device" />
                        </ListItemIcon>
                        <ListItemText
                            primary={
                                <div>
                                    App name
                                    <br />
                                    <b>Phio Access Plus</b>
                                </div>
                            }
                        />
                    </ListItem>

                    <ListItem>
                        <ListItemIcon>
                            <img src={versionIcon} alt="version" />
                        </ListItemIcon>
                        <ListItemText
                            primary={
                                <Typography style={{ fontWeight: 'bold' }}>
                                    {medicalDeviceVersionStr}
                                </Typography>
                            }
                        />
                    </ListItem>
                    <ListItem alignItems="flex-start">
                        <ListItemIcon>
                            <img src={manufacturerIcon} alt="manufacturer" />
                        </ListItemIcon>
                        <ListItemText
                            primary={
                                <div>
                                    Manufacturer
                                    <br />
                                    <b>
                                        EQL
                                        <br />
                                        16 Eaton Ave
                                        <br />
                                        Buckshaw Village
                                        <br />
                                        Chorley
                                        <br />
                                        PR7 7NAQ
                                    </b>
                                </div>
                            }
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon>
                            <img src={releaseIcon} alt="release" />
                        </ListItemIcon>
                        <ListItemText
                            primary={
                                <div>
                                    Date of release
                                    <br />
                                    <b>14/05/2021</b>
                                </div>
                            }
                        />
                    </ListItem>
                </List>
            </>
        );
    }

    if (isPhioTriageAgent) {
        return (
            <>
                <Typography variant="h5" component="h1" gutterBottom fontWeight="bold">
                    About Phio Triage Portal
                </Typography>
                <Typography variant="body1" sx={{ marginTop: '16px' }} mb={2}>
                    The Portal gives authorised users access to patient triage data, clinical
                    management tools, and administrative functions for the Phio Triage platform.
                </Typography>
                <Typography variant="h6" component="h2" gutterBottom fontWeight="bold">
                    How to use the Portal
                </Typography>
                <Typography variant="body1" mb={3}>
                    Use the portal to search for patients, view or download triage reports, manage
                    patient care pathways, refer patients into Phio Triage, or manage your
                    organisation's users and settings.
                </Typography>
                <Typography variant="h6" component="h2" gutterBottom fontWeight="bold">
                    When to use the Portal
                </Typography>
                <Typography variant="body1" mb={3}>
                    Use this portal once a patient has been referred into or has completed their
                    triage assessment in Phio Triage, or to manage your organisation's users and
                    configuration.
                </Typography>
                <Typography variant="h6" component="h2" gutterBottom fontWeight="bold">
                    How the Portal works
                </Typography>
                <Typography variant="body1" mb={3}>
                    The portal displays data captured during a patient's triage assessment and
                    allows you to generate a downloadable PDF report. Role-based access controls
                    ensure each user can only access the functionality relevant to their role.
                </Typography>
                <Typography variant="h6" component="h2" gutterBottom fontWeight="bold">
                    Our Policies
                </Typography>
                <Typography variant="body1">
                    Our policies tell you important information about using the portal and how we
                    use your data.
                </Typography>
                <Box mb={3}>
                    <div>
                        <Link
                            href="https://phio.eql.ai/privacy"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Privacy policy
                        </Link>
                    </div>
                </Box>
                <Typography variant="h6" component="h2" fontWeight="bold">
                    Software Information
                </Typography>
                <List>
                    <ListItem>
                        <ListItemIcon>
                            <img src={eqlIcon} alt="eql logo" />
                        </ListItemIcon>
                        <ListItemText
                            primary={
                                <div>
                                    App name
                                    <br />
                                    <b>Phio Triage</b>
                                </div>
                            }
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon>
                            <img src={versionIcon} alt="version" />
                        </ListItemIcon>
                        <ListItemText
                            primary={
                                <div>
                                    Version
                                    <br />
                                    <b>{medicalDeviceVersionStr}</b>
                                </div>
                            }
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon>
                            <img src={manufacturerIcon} alt="manufacturer" />
                        </ListItemIcon>
                        <ListItemText
                            primary={
                                <div>
                                    Manufacturer
                                    <br />
                                    <b>
                                        EQL Ltd.
                                        <br />
                                        16 Eaton Avenue, Matrix Park
                                        <br />
                                        Chorley, Lancashire, PR7 7NA
                                        <br />
                                        United Kingdom
                                    </b>
                                </div>
                            }
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon>
                            <img src={releaseIcon} alt="release" />
                        </ListItemIcon>
                        <ListItemText
                            primary={
                                <div>
                                    Date of Release
                                    <br />
                                    <b>{medicalDeviceReleaseDateStr}</b>
                                </div>
                            }
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon>
                            <img src={infoBookIcon} alt="instructions for use" />
                        </ListItemIcon>
                        <ListItemText
                            primary={
                                <div>
                                    Instructions for Use
                                    <br />
                                    <div className="">
                                        <Link
                                            href={instructionsForUseUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            View instructions for use
                                        </Link>
                                    </div>
                                </div>
                            }
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon>
                            <img src={UkCaIcon} alt="uk ca" />
                        </ListItemIcon>
                        <ListItemIcon>
                            <img src={medicalDeviceIcon} alt="medical device" />
                        </ListItemIcon>
                    </ListItem>
                </List>
            </>
        );
    }

    return null;
};

export default AboutThisPortal;
